import dayjs from 'dayjs';
import {
    BovineApi,
    BreedApi,
    FarmApi,
    FarmLotApi,
    OwnerApi,
    ServiceApi,
} from '../services/api';

const getChipValue = (field, value) => {
    return `${field}: ${value}`;
};

const mapValueId = (value) => value.map((value) => value.id);

const mapChipsName = (name, value) =>
    value.map((type) => getChipValue(name, type.name));

const fetchOptionsByName = (callback) => {
    return async ({ query: name } = {}, _, apiOptions) => {
        return (
            await callback({
                name,
                orderField: 'name',
                order: 'asc',
            }, apiOptions)
        ).results;
    };
};

const filterValueByName = (valueList, value) =>
    valueList.filter((v) => v.name !== value);

const buildDateRangeFilter = (value) => {
    if (!value.find((val) => val)) {
        return undefined;
    }
    const start = value[0]
        ? dayjs(value[0]).format('YYYY-MM-DD')
        : null;
    const end = value[1] ? dayjs(value[1]).format('YYYY-MM-DD') : null;
    return (end ? [start || 0, end] : [start]).join(',');
};

const buildDateRangeChips = (name, value) => {
    if (!value.find((val) => val)) {
        return [];
    }
    const formattedValue = value
        .filter((date) => date)
        .map((date) => dayjs(date).format('DD/MM/YYYY'))
        .join(' - ');
    return [getChipValue(name, formattedValue)];
};

const timeUnitOptions = [
    { name: 'Dias', value: 'd' },
    { name: 'Meses', value: 'M' },
    { name: 'Anos', value: 'y' },
];

export function useTermFilter() {
    return {
        value: [],
        name: 'Termo',
        query: (value) => ({ term: value }),
        chips: (name, value) => value.map((term) => getChipValue(name, term)),
        removeValue: (valueList, value) => valueList.filter((t) => t !== value),
    };
}

export function useEarringFilter() {
    return {
        value: '',
        name: 'Brinco',
        query: (value) => ({
            earring: value.split(',').filter(Number).map(Number),
        }),
        chips: (name, value) => [getChipValue(name, value)],
        removeValue: () => '',
    };
}

export function useExcludeEarringFilter() {
    return {
        value: '',
        name: 'Excluir Brinco',
        query: (value) => ({
            excludeEarring: value.split(',').filter(Number).map(Number).join(','),
        }),
        chips: (name, value) => [getChipValue(name, value)],
        removeValue: () => '',
    };
}

export function useTypeFilter() {
    return {
        value: [],
        name: 'Tipo',
        options: [],
        fetchOptions: fetchOptionsByName(BovineApi.fetchTypes),
        query: (value) => ({ typeId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useBreedFilter() {
    return {
        value: [],
        name: 'Raça',
        options: [],
        fetchOptions: fetchOptionsByName(BreedApi.fetch),
        query: (value) => ({ breedId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useWeightFilter() {
    return {
        value: [null, null],
        name: 'Peso',
        query: (value) => {
            if (!value.find((val) => Number(val) >= 0)) {
                return {};
            }
            return {
                weight: value.join(','),
            };
        },
        chips: (name, value) => {
            if (!value.find((val) => val)) {
                return [];
            }
            const values = value.map(val => val.toLocaleString()).join(' - ');
            return [getChipValue(name, values)];
        },
        removeValue: () => [null, null],
    };
}

export function useBirthDateFilter() {
    return {
        value: [null, null],
        name: 'Nascimento',
        query: (value) => ({
            birthDate: buildDateRangeFilter(value),
        }),
        chips: buildDateRangeChips,
        removeValue: () => [null, null],
    };
}

export function useAgeFilter() {
    return {
        value: [null, null, 'd'],
        name: 'Idade',
        options: timeUnitOptions,
        query: (value) => {
            if (!value[0] && !value[1]) {
                return {};
            }
            return { age: [(value[0] || 0), (value[1] || 0), value[2]].join(',') };
        },
        chips: (name, value) => {
            if (!value[0] && !value[1]) {
                return [];
            }
            const rangeValues = value.slice(0, 2).map(val => Number(val).toLocaleString()).join(' - ');
            const unitOption = timeUnitOptions.find((option) => option.value === value[2]);
            const formattedValues = `${rangeValues} ${(unitOption?.name || '').toLocaleLowerCase()}`;
            return [getChipValue(name, formattedValues)];
        },
        removeValue: () => [null, null, 'd'],
    };
}

export function useLastChildBirthFilter() {
    return {
        value: [null, null, 'd'],
        name: 'Parto',
        options: timeUnitOptions,
        query: (value) => {
            if (!value[0] && !value[1]) {
                return {};
            }
            return { lastChildBirth: [(value[0] || 0), (value[1] || 0), value[2]].join(',') };
        },
        chips: (name, value) => {
            if (!value[0] && !value[1]) {
                return [];
            }
            const rangeValues = value.slice(0, 2).map(val => Number(val).toLocaleString()).join(' - ');
            const unitOption = timeUnitOptions.find((option) => option.value === value[2]);
            const formattedValues = `${rangeValues} ${(unitOption?.name || '').toLocaleLowerCase()}`;
            return [getChipValue(name, formattedValues)];
        },
        removeValue: () => [null, null, 'd'],
    };
}

export function useStatusFilter() {
    return {
        value: [],
        name: 'Status',
        options: [],
        fetchOptions: fetchOptionsByName(BovineApi.fetchStatus),
        query: (value) => ({ statusId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useFarmFilter() {
    return {
        value: [],
        name: 'Fazenda',
        options: [],
        fetchOptions: fetchOptionsByName(FarmApi.fetch),
        query: (value) => ({ farmId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useFarmLotFilter() {
    return {
        value: [],
        name: 'Lote/Piquete',
        options: [],
        fetchOptions: async ({ value: name } = {}, other, apiOptions) => {
            const query = {
                name,
                orderField: 'name',
                order: 'asc',
            };
            if (other?.farms?.value?.length) {
                query.farmId = mapValueId(other?.farms?.value);
            }
            return (await FarmLotApi.fetch(query, apiOptions)).results;
        },
        query: (value) => ({ lotId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useOwnerFilter() {
    return {
        value: [],
        name: 'Proprietário',
        options: [],
        fetchOptions: fetchOptionsByName(OwnerApi.fetch),
        query: (value) => ({ ownerId: mapValueId(value) }),
        chips: mapChipsName,
        removeValue: filterValueByName,
    };
}

export function useRootEarringFilter() {
    return {
        value: '',
        name: 'Brinco Matriz',
        query: (value) => ({
            bovineRootEarring: value.split(',').filter(Number).map(Number),
        }),
        chips: (name, value) => [getChipValue(name, value)],
        removeValue: () => '',
    };
}

export function useIsRootFilter() {
    return {
        value: '',
        options: [
            { name: 'Sim', value: 1 },
            { name: 'Não', value: 0 },
        ],
        name: 'Vaca Matriz',
        query: (value) => ({ isBovineRoot: value.value === 1 }),
        chips: (name, value) => [getChipValue(name, value.name)],
        removeValue: () => 0,
    };
}

export function useGroupRootAndChildFilter() {
    return {
        value: '',
        options: [
            { name: 'Sim', value: 1 },
            { name: 'Não', value: 0 },
        ],
        name: 'Agrupar Matriz e Crias',
        query: (value) => ({ groupRootAndChild: value.value === 1 }),
        chips: (name, value) => [getChipValue(name, value.name)],
        removeValue: () => 0,
    };
}

export function useCreatedAtFilter() {
    return {
        value: [null, null],
        name: 'Cadastro',
        query: (value) => ({ createdAt: buildDateRangeFilter(value) }),
        chips: buildDateRangeChips,
        removeValue: () => [null, null],
    };
}

export function useUpdatedAtFilter() {
    return {
        value: [null, null],
        name: 'Atualizado',
        query: (value) => ({ updatedAt: buildDateRangeFilter(value) }),
        chips: buildDateRangeChips,
        removeValue: () => [null, null],
    };
}

export function useServiceFilter() {
    return {
        value: [],
        name: 'Serviço',
        options: [],
        fetchOptions: async (event = {}) => {
            const query = {
                id: event.id,
                term: event.query,
                orderField: 'id',
                order: 'desc',
            };
            const fetchResponse = await ServiceApi.fetch(query);
            return fetchResponse.results.map((result) => {
                const values = [result.id, result.type.path];
                if (result.dateStart) {
                    values.push(dayjs(result.dateStart).format('DD/MM/YYYY'));
                }
                return { ...result, name: values.join(' - ') };
            });
        },
        query: (value) => ({ serviceId: mapValueId(value).join(',') }),
        chips: (name, value) => value.map((type) => getChipValue(name, type.name)),
        removeValue: (valueList, value) => valueList.filter((v) => v.name !== value),
    };
}